import { Badge, Group, Tooltip } from '@mantine/core';
import { IconWifi, IconWifiOff, IconRefresh } from '@tabler/icons-react';
import { Text } from './index';

interface PresenceIndicatorProps {
  isConnected: boolean;
  isReconnecting?: boolean;
  reconnectAttempt?: number;
  maxReconnectAttempts?: number;
  lastConnected?: Date | null;
  error?: Error | null;
  showLabel?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg';
}

/**
 * Visual indicator for presence connection quality
 * Shows connection state with color coding and reconnection status
 */
export function PresenceIndicator({
  isConnected,
  isReconnecting = false,
  reconnectAttempt = 0,
  maxReconnectAttempts = 10,
  lastConnected,
  error,
  showLabel = true,
  size = 'sm',
}: PresenceIndicatorProps) {
  // Determine color and status
  const getStatus = () => {
    if (isConnected) {
      return {
        color: 'green',
        label: 'Connected',
        icon: <IconWifi size={14} />,
      };
    }

    if (isReconnecting) {
      return {
        color: 'yellow',
        label: `Reconnecting (${reconnectAttempt}/${maxReconnectAttempts})`,
        icon: <IconRefresh size={14} className="animate-spin" />,
      };
    }

    return {
      color: 'red',
      label: 'Disconnected',
      icon: <IconWifiOff size={14} />,
    };
  };

  const status = getStatus();

  // Build tooltip content
  const getTooltipContent = () => {
    const lines: string[] = [];

    lines.push(`Status: ${status.label}`);

    if (isConnected && lastConnected) {
      lines.push(`Connected since: ${lastConnected.toLocaleTimeString()}`);
    }

    if (!isConnected && lastConnected) {
      lines.push(`Last connected: ${lastConnected.toLocaleTimeString()}`);
    }

    if (isReconnecting) {
      lines.push(`Attempt ${reconnectAttempt} of ${maxReconnectAttempts}`);
    }

    if (error) {
      lines.push(`Error: ${error.message}`);
    }

    return lines.join('\n');
  };

  if (!showLabel) {
    // Compact mode - just the badge
    return (
      <Tooltip label={getTooltipContent()} multiline withArrow>
        <Badge
          size={size}
          color={status.color}
          variant="dot"
          style={{ cursor: 'help' }}
        />
      </Tooltip>
    );
  }

  // Full mode - badge with label
  return (
    <Tooltip label={getTooltipContent()} multiline withArrow>
      <Group gap="xs" style={{ cursor: 'help' }}>
        <Badge
          size={size}
          color={status.color}
          variant="light"
          leftSection={status.icon}
        >
          {status.label}
        </Badge>

        {error && (
          <Text size="xs" c="red">
            {error.message}
          </Text>
        )}
      </Group>
    </Tooltip>
  );
}
